import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  LinearProgress,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import TerminalIcon from '@mui/icons-material/Terminal';
import { useNavigate, useParams } from 'react-router-dom';
import { BASEURL, PRIMARYCOLOR } from '../../Constants';
import Nav from './nav';
import ExploitsModal from './exploitsModal';

const SEVERITY_ORDER = ['critical', 'high', 'medium', 'low', 'info', 'informational'];

const sevColor = (severity) => {
  const value = String(severity || '').toLowerCase();
  switch (value) {
    case 'critical':
      return '#9c27b0';
    case 'high':
      return '#f44336';
    case 'medium':
      return '#ff9800';
    case 'low':
      return '#2196f3';
    case 'info':
    case 'informational':
      return '#4caf50';
    default:
      return '#9e9e9e';
  }
};

const methodColor = {
  GET: '#DCEEFF',
  POST: '#E3F4E8',
  PUT: '#FFF3D5',
  DELETE: '#FFE3DE',
  PATCH: '#F2E6FF',
};

const methodTextColor = {
  GET: '#0B5CAD',
  POST: '#1C7C3F',
  PUT: '#9A5B00',
  DELETE: '#C0392B',
  PATCH: '#7A2CB5',
};

const parseLocation = (value) => {
  if (!value) return { path: '', method: '' };
  const parts = String(value).split(' - ');
  if (parts.length === 2) return { path: parts[0], method: parts[1].toUpperCase() };
  const match = String(value).match(/(.*)\s+-\s+([A-Z]+)$/i);
  if (match) return { path: match[1], method: match[2].toUpperCase() };
  return { path: String(value), method: '' };
};

const stripQueryString = (value) => String(value || '').split('?')[0].trim();

const copy = async (text) => {
  try {
    await navigator.clipboard.writeText(String(text || ''));
  } catch {}
};

const NiceBarRow = ({ label, value = 0, max = 1, onClick }) => {
  const percent = Math.min(100, Math.round((value / Math.max(1, max)) * 100));
  return (
    <Stack spacing={0.5} sx={{ cursor: onClick ? 'pointer' : 'default' }} onClick={onClick}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" sx={{ fontWeight: 600 }} title={label}>
          {label}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {value}
        </Typography>
      </Stack>
      <LinearProgress variant="determinate" value={percent} sx={{ height: 8, borderRadius: 999 }} />
    </Stack>
  );
};

const PentestWebExploits = () => {
  const [toolsData, setToolsData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchCategory, setSearchCategory] = useState('any');
  const [selectedExploit, setSelectedExploit] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState('report');

  const navigate = useNavigate();
  const { pentest_id } = useParams();

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }

    fetch(`${BASEURL}/pentests/web/exploits?pentest_id=${pentest_id}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data?.error && data?.message === 'Invalid token') navigate('/');
        else setToolsData(Array.isArray(data?.exploits) ? data.exploits : []);
      })
      .catch((error) => console.error('Error fetching data:', error));
  }, [navigate, pentest_id]);

  const rows = useMemo(() => {
    return (toolsData || []).map((row, index) => {
      const { path, method } = parseLocation(row.impacted_assets);
      const severity = String(row.severity || '').toLowerCase();
      return {
        id: row.id ?? `${index}-${row.name || ''}`,
        severity,
        finding: row.name || '',
        description: row.description || '',
        path,
        displayPath: stripQueryString(path),
        method,
        raw: row,
      };
    });
  }, [toolsData]);

  const sevCounts = useMemo(() => {
    const out = { critical: 0, high: 0, medium: 0, low: 0, info: 0 };
    rows.forEach((row) => {
      const severity = (row.severity || '').toLowerCase();
      if (severity === 'info' || severity === 'informational') out.info += 1;
      else if (out[severity] !== undefined) out[severity] += 1;
    });
    return out;
  }, [rows]);

  const topLocations = useMemo(() => {
    const map = new Map();
    rows.forEach((row) => {
      const key = row.displayPath || row.path || '—';
      map.set(key, (map.get(key) || 0) + 1);
    });
    return [...map.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5).map(([key, value]) => ({ key, value }));
  }, [rows]);

  const countByFinding = useMemo(() => {
    const map = {};
    rows.forEach((row) => {
      const key = row.finding || '—';
      map[key] = (map[key] || 0) + 1;
    });
    return Object.entries(map)
      .map(([key, value]) => ({ key, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);
  }, [rows]);

  const maxLocationCount = Math.max(1, ...topLocations.map((item) => item.value));
  const maxFindingCount = Math.max(1, ...countByFinding.map((item) => item.value));

  const filteredRows = useMemo(() => {
    if (!searchTerm) return rows;
    const query = searchTerm.toLowerCase();

    return rows.filter((row) => {
      const finding = (row.finding || '').toLowerCase();
      const severity = (row.severity || '').toLowerCase();
      const description = (row.description || '').toLowerCase();
      const location = `${row.path || ''} ${row.displayPath || ''}`.toLowerCase();
      const method = (row.method || '').toLowerCase();

      if (searchCategory === 'finding') return finding.includes(query);
      if (searchCategory === 'severity') return severity.includes(query);
      if (searchCategory === 'description') return description.includes(query);
      if (searchCategory === 'location') return location.includes(query);
      if (searchCategory === 'method') return method.includes(query);

      return (
        finding.includes(query) ||
        severity.includes(query) ||
        description.includes(query) ||
        location.includes(query) ||
        method.includes(query)
      );
    });
  }, [rows, searchCategory, searchTerm]);

  const sortedRows = useMemo(() => {
    return [...filteredRows].sort(
      (a, b) => SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity)
    );
  }, [filteredRows]);

  const openDrawer = (exploit, mode) => {
    setSelectedExploit(exploit);
    setDrawerMode(mode);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedExploit(null);
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', flexDirection: 'column' }}>
      <Nav />

      <Box sx={{ flexGrow: 1, p: { xs: 2, md: 3 } }}>
        <Stack spacing={2} sx={{ mb: 2 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                Exploits
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Confirmed web findings with report details and agent execution logs.
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
            {['critical', 'high', 'medium', 'low', 'info'].map((severity) => (
              <Card key={severity} sx={{ p: 2, borderRadius: 3, boxShadow: 2, minWidth: 180 }}>
                <Typography variant="overline" color="text.secondary">
                  {severity.toUpperCase()}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip size="small" sx={{ bgcolor: sevColor(severity), color: '#fff', fontWeight: 700 }} label={severity} />
                  <Typography variant="h5" sx={{ fontWeight: 800 }}>
                    {sevCounts[severity]}
                  </Typography>
                </Stack>
              </Card>
            ))}
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <Card sx={{ p: 2, borderRadius: 3, boxShadow: 2, flex: 1 }}>
              <Typography variant="overline" color="text.secondary">
                TOP 5 ENDPOINTS
              </Typography>
              <Stack spacing={1.25} sx={{ mt: 1 }}>
                {topLocations.length ? (
                  topLocations.map((row) => (
                    <NiceBarRow
                      key={row.key}
                      label={row.key}
                      value={row.value}
                      max={maxLocationCount}
                      onClick={() => {
                        setSearchCategory('location');
                        setSearchTerm(row.key);
                      }}
                    />
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No data
                  </Typography>
                )}
              </Stack>
            </Card>

            <Card sx={{ p: 2, borderRadius: 3, boxShadow: 2, flex: 1 }}>
              <Typography variant="overline" color="text.secondary">
                TOP 5 FINDINGS
              </Typography>
              <Stack spacing={1.25} sx={{ mt: 1 }}>
                {countByFinding.length ? (
                  countByFinding.map((row) => (
                    <NiceBarRow
                      key={row.key}
                      label={row.key}
                      value={row.value}
                      max={maxFindingCount}
                      onClick={() => {
                        setSearchCategory('finding');
                        setSearchTerm(row.key);
                      }}
                    />
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No data
                  </Typography>
                )}
              </Stack>
            </Card>
          </Stack>

          <Divider />
        </Stack>

        <Card sx={{ p: 2, borderRadius: 3, mt: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                Exploit Inventory
              </Typography>
              <Chip
                size="small"
                label={`${sortedRows.length}/${rows.length || 0} shown`}
                sx={{ ml: 1, fontWeight: 700 }}
                variant="outlined"
              />
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Filter By</InputLabel>
                <Select value={searchCategory} label="Filter By" onChange={(event) => setSearchCategory(event.target.value)}>
                  <MenuItem value="any">Any</MenuItem>
                  <MenuItem value="finding">Finding</MenuItem>
                  <MenuItem value="severity">Severity</MenuItem>
                  <MenuItem value="description">Description</MenuItem>
                  <MenuItem value="location">Location</MenuItem>
                  <MenuItem value="method">Method</MenuItem>
                </Select>
              </FormControl>

              <TextField
                size="small"
                placeholder="Search… (e.g., XSS, high, /api/users, POST)"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                sx={{ ml: 1, minWidth: 320 }}
              />
              <Button variant="outlined" onClick={() => setSearchTerm('')}>
                Clear
              </Button>
            </Stack>
          </Stack>

          <Divider sx={{ my: 2 }} />

          <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 6, overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 1220 }}>
              <TableHead sx={{ backgroundColor: PRIMARYCOLOR }}>
                <TableRow>
                  <TableCell sx={{ color: 'white', fontWeight: 800, width: 130 }}>Severity</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 800, width: 320 }}>Finding</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 800, width: 440 }}>Description</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 800, width: 320 }}>Endpoint</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 800, width: 120 }}>Method</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 800, width: 150 }}>Action</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {sortedRows.length ? (
                  sortedRows.map((row) => (
                    <TableRow
                      key={row.id}
                      hover
                      sx={{ '&:hover': { backgroundColor: 'action.hover' }, verticalAlign: 'top' }}
                    >
                      <TableCell>
                        <Chip
                          size="small"
                          label={row.severity ? row.severity.toUpperCase() : '—'}
                          sx={{ bgcolor: sevColor(row.severity), color: '#fff', fontWeight: 800 }}
                        />
                      </TableCell>

                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="flex-start">
                          <Tooltip title={row.finding || ''} placement="top" arrow>
                            <Typography
                              variant="body2"
                              sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                fontWeight: 700,
                                lineHeight: 1.35,
                              }}
                            >
                              {row.finding || '—'}
                            </Typography>
                          </Tooltip>

                          {row.finding ? (
                            <Tooltip title="Copy finding" arrow>
                              <IconButton size="small" onClick={() => copy(row.finding)} sx={{ mt: '-2px' }}>
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="flex-start">
                          <Tooltip title={row.description || ''} placement="top" arrow>
                            <Typography
                              variant="body2"
                              sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                lineHeight: 1.35,
                              }}
                            >
                              {row.description || '—'}
                            </Typography>
                          </Tooltip>

                          {row.description ? (
                            <Tooltip title="Copy description" arrow>
                              <IconButton size="small" onClick={() => copy(row.description)} sx={{ mt: '-2px' }}>
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
                          {row.displayPath || row.path || '—'}
                        </Typography>
                        {row.path && row.displayPath !== row.path && (
                          <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                            {row.path}
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        {row.method ? (
                          <Chip
                            size="small"
                            label={row.method}
                            sx={{
                              bgcolor: methodColor[row.method] || '#e0e0e0',
                              color: methodTextColor[row.method] || '#000',
                              fontWeight: 800,
                            }}
                          />
                        ) : (
                          '—'
                        )}
                      </TableCell>

                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Tooltip title="Open report (Markdown)" arrow>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => openDrawer(row.raw, 'report')}
                                disabled={!row.raw?.report_details}
                              >
                                <DescriptionOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>

                          <Tooltip title="Open agent logs / details" arrow>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => openDrawer(row.raw, 'logs')}
                                disabled={!row.raw?.command_output}
                              >
                                <TerminalIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography>No data available</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Box>

      <ExploitsModal open={drawerOpen} handleClose={closeDrawer} exploit={selectedExploit} mode={drawerMode} />
    </Box>
  );
};

export default PentestWebExploits;
